import React from 'react'
import styled from 'styled'
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd"

import * as entity from 'entity'

import { ListItem as PListItem } from 'domains/org/getty/components/points-of-interest/list-item'
import { ListItem as EListItem } from 'domains/org/getty/components/event-points-of-interest/list-item'
import { ListItem as GListItem } from 'domains/org/getty/components/generic-points-of-interest/list-item'

import { StopPointOfInterest } from 'generated/mos/org/getty/audio'
import { EventPointOfInterest } from 'generated/mos/org/getty/audio'
import { GenericPointOfInterest } from 'generated/mos/org/getty/audio'

import { Placement, SpacePlacement } from 'generated/mos/placement'
import { Space, Site } from 'generated/mos/structure'

import { dndReorder } from 'helpers/drag-and-drop'
import { space } from 'helpers/style'


const getScrollbarWidth = () => window.innerWidth - document.documentElement.clientWidth;
const NewPointsOfInterestDivider = styled.div`
  position: relative;
  margin-bottom: ${space(3)};
  font-size: ${({ theme }) => theme.font.size.xSmall};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  color: ${({ theme }) => theme.color.primary};
  & span {
    position: relative;
    padding-right: ${space(2)};
    background-color: ${({ theme }) => theme.color.grayL90};
  }
  & hr {
    position: absolute;
    top: 50%;
    margin: 0;
    width: calc(50% + 50vw - ${() => getScrollbarWidth() / 2}px);
    border-bottom: 1px solid ${({ theme }) => theme.color.primary};
  }
`;

type Props = {
  readonly projectId: string;
  readonly moodId: string;
  readonly newlyCreatedPointOfInterestRef?: StopPointOfInterest.Entity['ref'] | GenericPointOfInterest.Entity['ref'] | EventPointOfInterest.Entity['ref'];
  readonly historyPush: (to: string) => void;
  readonly pointsOfInterest: ReadonlyArray<StopPointOfInterest.Entity | EventPointOfInterest.Entity | GenericPointOfInterest.Entity>;
  readonly setOrderedPointsOfInterest: React.Dispatch<React.SetStateAction<ReadonlyArray<StopPointOfInterest.Entity | EventPointOfInterest.Entity | GenericPointOfInterest.Entity>>>;
  readonly deleteFromMood?: (pointOfInterestRef: StopPointOfInterest.Ref | GenericPointOfInterest.Ref | EventPointOfInterest.Ref) => void;
  readonly spacesListData: readonly Space.Entity[] | undefined;
  readonly sitesData: ReadonlyArray<Site.Entity> | undefined;
};

export const PointsOfInterestList = ({ pointsOfInterest, setOrderedPointsOfInterest, newlyCreatedPointOfInterestRef, deleteFromMood, spacesListData, sitesData, ...rest }: Props) => {
  const [hasDragged, setHasDragged] = React.useState(false);
  const lastScrolledFor = React.useRef<entity.Ref>();
  const listBottomRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!newlyCreatedPointOfInterestRef) return;
    const newPointOfInterestHasLoaded = pointsOfInterest.some(
      pointOfInterest => pointOfInterest.ref && pointOfInterest.ref.id === newlyCreatedPointOfInterestRef.id
    );
    const newPointOfInterestIsNew = !lastScrolledFor.current
      ? true
      : lastScrolledFor.current.id !== newlyCreatedPointOfInterestRef.id;

    if (newPointOfInterestHasLoaded && newPointOfInterestIsNew) {
      lastScrolledFor.current = newlyCreatedPointOfInterestRef;
      setHasDragged(false);
      requestAnimationFrame(() => {
        if (null !== listBottomRef.current) {
          listBottomRef.current.scrollIntoView({behavior: 'smooth'})
	}
      });
    }
  }, [newlyCreatedPointOfInterestRef, pointsOfInterest]);

  const isDividerVisible = (index: number) =>
    !hasDragged &&
    newlyCreatedPointOfInterestRef &&
    entity.isRefEqual(pointsOfInterest[index].ref, newlyCreatedPointOfInterestRef);

  const resolvePlacement = (placement: Placement.Entity) => {
    const resolved = spacesListData && spacesListData.find((item: Space.Entity) => {
      const id = Space.mustRef(item.ref).id
      return placement &&
        placement.type === Placement.refName &&
        placement.placement &&
        placement.placement.type === SpacePlacement.refName &&
        placement.placement.spaceRef &&
        id === placement.placement.spaceRef.id
    })
    const site = resolved && resolved.siteRef && sitesData && sitesData.find(({ ref }: Site.Entity) => resolved.siteRef && Site.mustRef(ref).id === resolved.siteRef.id)
    return {
      placement: resolved ? resolved.displayName || resolved.name : undefined,
      site: site ? site.name : undefined
    }
  }
 
  return (
    <>
      <DragDropContext
        onBeforeDragStart={() => !hasDragged && setHasDragged(true)}
        onDragEnd={dropResult => setOrderedPointsOfInterest(dndReorder(dropResult, pointsOfInterest))}
      >
        <Droppable droppableId="pointOfInterests-list">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {pointsOfInterest.map((pointOfInterest, index) => {
              if (pointOfInterest.type == 'mos.org.getty.audio.EventPointOfInterest') {
                const resolvedLocations = pointOfInterest.placement && resolvePlacement(pointOfInterest.placement.value as Placement.Entity);
                return (
                  <React.Fragment key={pointOfInterest.ref && pointOfInterest.ref.id}>
                    {isDividerVisible(index) && (
                      <NewPointsOfInterestDivider>
                        <hr /><span>Newly added points of interest</span>
                      </NewPointsOfInterestDivider>
                    )}
                    <Draggable draggableId={pointOfInterest.ref ? pointOfInterest.ref.id : ''} index={index}>
                      {(provided, snapshot) => {
                        const resolvedLocations = pointOfInterest.placement && resolvePlacement(pointOfInterest.placement.value as Placement.Entity)
                        return (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <EListItem
                              {...rest}
                              draggableIndex={index}
                              site={resolvedLocations && resolvedLocations.site}
                              placement={resolvedLocations && resolvedLocations.placement}
                              deleteFromMood={deleteFromMood}
                              isDragging={snapshot.isDragging}
                              pointOfInterest={pointOfInterest}
                            />
                          </div>
                        )
                      }
                    }
                    </Draggable>
                  </React.Fragment>
                )
              } else if (pointOfInterest.type == 'mos.org.getty.audio.GenericPointOfInterest') {
                const resolvedLocations = pointOfInterest.placement && resolvePlacement(pointOfInterest.placement.value as Placement.Entity);
                return (
                  <React.Fragment key={pointOfInterest.ref && pointOfInterest.ref.id}>
                    {isDividerVisible(index) && (
                      <NewPointsOfInterestDivider>
                        <hr /><span>Newly added points of interest</span>
                      </NewPointsOfInterestDivider>
                    )}
                    <Draggable draggableId={pointOfInterest.ref ? pointOfInterest.ref.id : ''} index={index}>
                      {(provided, snapshot) => {
                        const resolvedLocations = pointOfInterest.placement && resolvePlacement(pointOfInterest.placement.value as Placement.Entity)
                        return (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <GListItem
                              {...rest}
                              draggableIndex={index}
                              site={resolvedLocations && resolvedLocations.site}
                              placement={resolvedLocations && resolvedLocations.placement}
                              deleteFromMood={deleteFromMood}
                              isDragging={snapshot.isDragging}
                              pointOfInterest={pointOfInterest}
                            />
                          </div>
                        )
                      }
                    }
                    </Draggable>
                  </React.Fragment>
                )
              } else if (pointOfInterest.type == 'mos.org.getty.audio.StopPointOfInterest') {
                const resolvedLocations = pointOfInterest.stop && pointOfInterest.stop.placement && resolvePlacement(pointOfInterest.stop && pointOfInterest.stop.placement.value as Placement.Entity)
                return (
                  <React.Fragment key={pointOfInterest.ref && pointOfInterest.ref.id}>
                    {isDividerVisible(index) && (
                      <NewPointsOfInterestDivider>
                        <hr /><span>Newly added points of interest</span>
                      </NewPointsOfInterestDivider>
                    )}
                    <Draggable draggableId={pointOfInterest.ref ? pointOfInterest.ref.id : ''} index={index}>
                      {(provided, snapshot) => {
                        return (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <PListItem
                              {...rest}
                              draggableIndex={index}
                              site={resolvedLocations && resolvedLocations.site}
                              placement={resolvedLocations && resolvedLocations.placement}
                              deleteFromMood={deleteFromMood}
                              isDragging={snapshot.isDragging}
                              pointOfInterest={pointOfInterest}
                            />
                          </div>
                        )
                      }
                    }
                    </Draggable>
                  </React.Fragment>
                )
              }
              })}
              {/* handy div for knowing position of bottom of list*/}
              <div ref={listBottomRef} />
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  )
};